import { create } from "zustand";
import { IPaginationMeta, IProject } from "../@types";
import { debounce } from "lodash";
import { Filter } from "../@types/invoice";
import { projectService } from "../services/projectService";

interface FetchOptions {
  startDate?: string;
  endDate?: string;
  actionType?: "initial" | "filter";
}

interface ProjectStore {
  projects: IProject[];
  project: IProject;
  hasData: boolean;
  isLoading: boolean;
  isLoadingInitial: boolean;
  isLoadingFilter: boolean;
  isLoadingMore: boolean;
  error: any;
  createProject: (project: Partial<IProject>) => Promise<void>;
  fetchProjects: () => Promise<void>;
  fetchData: (options?: FetchOptions) => Promise<void> | undefined;
  fetchMoreData: () => Promise<void>;
  setSearchText: (text: string) => void;
  handleSort: (text: string) => void;
  setFilter: (filter: Filter) => void;
  removeProject: (id: string) => void;
  addProject: (invoice: any) => void;
  pagination: IPaginationMeta;
  searchText: string;
  range: string;
  filter: Filter;
  getProjectById: (id: string) => Promise<IProject | null>;
  clearAllFilters: () => void;
  deleteProject: (id: string) => Promise<void>;
}

export const useProjectStore = create<ProjectStore>((set, get) => ({
  projects: [],
  project: {} as IProject,
  hasData: false,
  newServices: [],
  isLoading: false,
  isLoadingInitial: true,
  isLoadingFilter: false,
  isLoadingMore: false,
  error: null,
  searchText: "",
  range: "",
  filter: {
    status: "",
  },
  pagination: {
    cursor: null,
    backCursor: null,
    hasMoreData: false,
    limit: undefined,
    count: undefined,
  },
  fetchData: async (options?: FetchOptions) => {
    const { startDate, endDate, actionType = "initial" } = options || {};

    // Set loading flag only once at the start
    set((state) => ({
      ...state,
      isLoadingInitial: actionType === "initial",
      isLoadingFilter: actionType === "filter",
      isLoading: true,
      error: null,
    }));

    const { searchText, filter, range } = get();
    const hasFilters = range.length > 0 || searchText.length > 0;

    // Build query more safely
    const params = new URLSearchParams();
    if (searchText) params.append("search", searchText);
    if (range) params.append("range", range);
    if (startDate && endDate) {
      params.append("startDate", startDate);
      params.append("endDate", endDate);
    }

    let query = `/projects`;
    if (params.toString()) query += `?${params.toString()}`;

    try {
      const res = await projectService.get(query);
      const projects = res.data?.data ?? res.data;
      const hasData = !searchText && !hasFilters && projects.length > 0;

      set((state) => ({
        ...state,
        projects,
        hasData,
        pagination: res.data.meta,
        isLoadingInitial: false,
        isLoadingFilter: false,
        isLoading: false,
      }));
    } catch (e: any) {
      set((state) => ({
        ...state,
        isLoadingInitial: false,
        isLoadingFilter: false,
        isLoading: false,
        error: e.message,
      }));
    }
  },

  fetchProjects: async () => {
    set((prevState: ProjectStore) => ({
      ...prevState,
      isLoading: true,
    }));
    try {
      const res = await projectService.get(`/projects`);
      set((prevState: ProjectStore) => ({
        ...prevState,
        inventory: res.data.data,
        pagination: res.data.meta,
        isLoading: false,
      }));
    } catch (e: any) {
      set((prevState: ProjectStore) => ({
        ...prevState,
        isLoading: false,
        error: e.message,
      }));
    }
  },
  createProject: async (projectData: Partial<IProject>) => {
    set((state) => ({ ...state, isLoading: true, error: null }));

    try {
      const res = await projectService.post("/projects", projectData);
      // Instead of re-fetching everything, just add the new project to state
      set((state) => ({
        ...state,
        projects: [res.data, ...state.projects],
        isLoading: false,
      }));
      return res.data;
    } catch (e: any) {
      set((state) => ({
        ...state,
        isLoading: false,
        error: e.message,
      }));
      throw new Error(e?.response?.data ?? "");
    }
  },
  fetchMoreData: async () => {
    const { filter } = get();

    if (!get()?.pagination?.hasMoreData || get().isLoadingMore) return;
    set({ isLoadingMore: true });
    const cursor = get().pagination.cursor;
    try {
      let query = `/projects?cursor=${cursor}`;
      if ("status" in filter && filter.status.length > 0) {
        if ("status" in filter && filter.status.length > 0)
          query += `&status=${filter.status}`;
      }

      const res = await projectService.get(query);
      set((state) => ({
        projects: [...state.projects, ...res.data.data],
        pagination: res.data.meta,
        isLoadingMore: false,
      }));
    } catch (e: any) {
      set({ isLoadingMore: false, error: e.message });
    }
  },
  setSearchText: (text: string) => {
    set({ searchText: text });
  },
  handleSort: (text: string) => {
    set({ range: text });
  },
  setFilter: (filter: Filter) => {
    set({ filter: filter as Filter });
  },
  removeProject: (id) => {
    const projects = get().projects;
    set({ projects: projects.filter((project) => project.id !== id) });
  },
  addProject: (project) => {
    set((state) => ({ projects: [project, ...state.projects] }));
  },
  getProjectById: async (id: string) => {
    set({ isLoading: true });
    try {
      const res = await projectService.get(`/projects/${id}`);
      set({ isLoading: false, project: res.data });
      return res.data;
    } catch (e: any) {
      console.error(e.response.data);
      set({ isLoading: false, error: e.message });
      return null;
    }
  },
  deleteProject: async (id: string) => {
    set({ isLoading: true });
    try {
      const res = await projectService.delete(`/projects/${id}`);
      get().removeProject(id);
      return res.data;
    } catch (e: any) {
      console.error(e.response.data);
      set({ isLoading: false, error: e.message });
      return null;
    }
  },
  clearAllFilters: () => {
    set({
      filter: {
        status: "",
      },
    });
  },
}));
